import useGetApi from '../useGetApi';
import useMutationApi from '../useMutationApi';
import { useQueryClient } from '@tanstack/react-query';
import config from 'src/config';
import type {
  PaymentAllocation,
  CreateAllocationRequest,
  BulkCreateAllocationsRequest,
  UpdateAllocationRequest,
} from './types';

// Query keys
export const paymentAllocationKeys = {
  all: ['paymentAllocations'] as const,
  byPayment: (paymentId: string) =>
    [...paymentAllocationKeys.all, 'byPayment', paymentId] as const,
  byAgreement: (agreementId: string) =>
    [...paymentAllocationKeys.all, 'byAgreement', agreementId] as const,
};

/**
 * Fetch allocations for a specific payment
 */
export function usePaymentAllocations(paymentId: string | null) {
  return useGetApi<PaymentAllocation[]>({
    endpoint: paymentId
      ? `rebates/supplier-payments/${paymentId}/allocations`
      : '',
    queryKey: paymentId ? paymentAllocationKeys.byPayment(paymentId) : [],
    options: { enabled: !!paymentId },
  });
}

/**
 * Create a single allocation
 */
export function useCreateAllocation(paymentId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutationApi<CreateAllocationRequest, { id: string }>({
    endpoint: `rebates/supplier-payments/${paymentId}/allocations`,
    method: 'POST',
  });

  const wrappedMutate = async (data: CreateAllocationRequest) => {
    const result = await mutation.mutateAsync(data);
    // Invalidate allocations for this payment
    queryClient.invalidateQueries({
      queryKey: paymentAllocationKeys.byPayment(paymentId),
    });
    // Invalidate all supplier payment queries (list views, single payment, etc.)
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].includes('rebates/supplier-payments'),
    });
    return result;
  };

  return {
    ...mutation,
    mutateAsync: wrappedMutate,
  };
}

/**
 * Bulk create allocations
 */
export function useBulkCreateAllocations(paymentId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutationApi<
    BulkCreateAllocationsRequest,
    { ids: string[] }
  >({
    endpoint: `rebates/supplier-payments/${paymentId}/allocations/bulk`,
    method: 'POST',
  });

  const wrappedMutate = async (data: BulkCreateAllocationsRequest) => {
    const result = await mutation.mutateAsync(data);
    // Invalidate allocations for this payment
    queryClient.invalidateQueries({
      queryKey: paymentAllocationKeys.byPayment(paymentId),
    });
    // Invalidate all supplier payment queries
    queryClient.invalidateQueries({
      queryKey: ['supplierPayments'],
    });
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].includes('rebates/supplier-payments'),
    });
    return result;
  };

  return {
    ...mutation,
    mutateAsync: wrappedMutate,
  };
}

/**
 * Update an allocation
 */
export function useUpdateAllocation(paymentId: string, allocationId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutationApi<
    UpdateAllocationRequest,
    { success: boolean }
  >({
    endpoint: `rebates/allocations/${allocationId}`,
    method: 'PUT',
  });

  const wrappedMutate = async (data: UpdateAllocationRequest) => {
    const result = await mutation.mutateAsync(data);
    // Invalidate allocations for this payment
    queryClient.invalidateQueries({
      queryKey: paymentAllocationKeys.byPayment(paymentId),
    });
    // Invalidate all supplier payment queries (list views, single payment, etc.)
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].includes('rebates/supplier-payments'),
    });
    return result;
  };

  return {
    ...mutation,
    mutateAsync: wrappedMutate,
  };
}

/**
 * Delete an allocation
 */
export function useDeleteAllocation(paymentId: string, allocationId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutationApi<void, void>({
    endpoint: `rebates/allocations/${allocationId}`,
    method: 'DELETE',
  });

  const wrappedMutate = async () => {
    const result = await mutation.mutateAsync();
    // Invalidate allocations for this payment
    queryClient.invalidateQueries({
      queryKey: paymentAllocationKeys.byPayment(paymentId),
    });
    // Invalidate all supplier payment queries
    queryClient.invalidateQueries({
      queryKey: ['supplierPayments'],
    });
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].includes('rebates/supplier-payments'),
    });
    return result;
  };

  return {
    ...mutation,
    mutateAsync: wrappedMutate,
  };
}

/**
 * Replace all allocations for a payment (delete existing + create new)
 * Deletes existing allocations and creates new ones in a transaction-like manner
 */
export function useReplaceAllocations(paymentId: string) {
  const queryClient = useQueryClient();
  const bulkCreate = useBulkCreateAllocations(paymentId);

  const replaceAllocations = async (data: {
    existingAllocationIds: string[];
    newAllocations: BulkCreateAllocationsRequest;
  }) => {
    // Delete all existing allocations first
    if (data.existingAllocationIds.length > 0) {
      const deleteResults = await Promise.all(
        data.existingAllocationIds.map(async (allocationId) => {
          const response = await fetch(
            `${config.apiBaseUrl}/rebates/allocations/${allocationId}`,
            {
              method: 'DELETE',
              credentials: 'include',
            },
          );
          if (!response.ok) {
            throw new Error(
              `Failed to delete allocation ${allocationId}: ${response.statusText}`,
            );
          }
          return response;
        }),
      );
    }

    // Then create new allocations
    const result = await bulkCreate.mutateAsync(data.newAllocations);

    // Explicitly invalidate all relevant queries
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: paymentAllocationKeys.byPayment(paymentId),
      }),
      queryClient.invalidateQueries({
        queryKey: ['supplierPayments'],
      }),
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].includes('rebates/supplier-payments'),
      }),
    ]);

    return result;
  };

  return {
    mutateAsync: replaceAllocations,
    isPending: bulkCreate.isPending,
    isError: bulkCreate.isError,
    error: bulkCreate.error,
  };
}

/**
 * Fetch all allocations for an agreement (for global context)
 */
export function useAllocationsByAgreement(agreementId: string | null) {
  return useGetApi<PaymentAllocation[]>({
    endpoint: agreementId
      ? `rebates/agreements/${agreementId}/allocations`
      : '',
    queryKey: agreementId ? paymentAllocationKeys.byAgreement(agreementId) : [],
    options: { enabled: !!agreementId },
  });
}
